import { format } from "date-fns";
import { DateTime } from "luxon";

export const GetSortedWorkingHours = (workingHours, userTimeZone) => {
    const defaultDisplayStart = "09:00 am";
    const defaultDisplayEnd = "05:00 pm";

    const convertedWorkingHours = workingHours.map((hour) => ({
        ...hour,
        startTime: hour?.onJob
            ? (ConvertUTCToLocal12Hour(hour.startTime, userTimeZone) || defaultDisplayStart)
            : ConvertUTCToLocal12Hour(hour.startTime, userTimeZone),
        endTime: hour?.onJob
            ? (ConvertUTCToLocal12Hour(hour.endTime, userTimeZone) || defaultDisplayEnd)
            : ConvertUTCToLocal12Hour(hour.endTime, userTimeZone),
        groupId: Number(hour.groupId),
        parentId: hour.parentId ? Number(hour.parentId) : null
    }));

    const parents = convertedWorkingHours.filter((hour) => hour.parentId === null);
    const children = convertedWorkingHours.filter((hour) => hour.parentId !== null);

    // Sort parents by groupId and then by id
    const sortedParents = parents.sort((a, b) => {
        if (a.groupId === b.groupId) {
            return a.id - b.id;
        }
        return a.groupId - b.groupId;
    });

    // Sort children by parentId and then by startTime
    // Sort children by parentId and then by id
    const sortedChildren = children.sort((a, b) => {
        if (a.parentId === b.parentId) {
            return a.id - b.id; // Sort by id when parentIds are equal
        }
        return a.parentId - b.parentId; // Otherwise, sort by parentId
    });

    // Create an array to hold the final display order
    const sortedWorkingHours = sortedParents.map((parent) => {
        const parentChildren = sortedChildren.filter(child => Number(child.parentId) === Number(parent.id));
        return {
            parent,
            children: parentChildren,
        };
    });

    return sortedWorkingHours;
};

// Convert a UTC time while keeping the same local date in the target time zone.
const ConvertToFixedLocalTime = (utcTime, originalTimeZoneObj, targetTimeZoneObj) => {
    const originalTimeZone = originalTimeZoneObj.timezone;
    const targetTimeZone = targetTimeZoneObj.timezone;

    if (!originalTimeZone || !targetTimeZone) {
        console.error("Invalid time zone objects:", { originalTimeZoneObj, targetTimeZoneObj });
        return null;
    }

    // Step 1: Convert UTC time to the original time zone
    const originalLocalTime = DateTime.fromFormat(utcTime, "HH:mm", { zone: "UTC" }).setZone(originalTimeZone);

    if (!originalLocalTime.isValid) {
        console.error("Invalid UTC time format:", utcTime);
        return null;
    }

    // Step 2: Extract the local time as a string in 12-hour format
    const localTime = originalLocalTime.toFormat("hh:mm a"); // 12-hour format with AM/PM

    // Step 3: Assign the same local time to the target time zone
    const fixedLocalTimeInTargetZone = DateTime.fromFormat(localTime, "hh:mm a", {
        zone: targetTimeZone,
    });

    // Step 4: Return the fixed local time in the target time zone
    return fixedLocalTimeInTargetZone.toFormat("hh:mm a"); // Final output in 12-hour format with AM/PM
};




// Convert a UTC time while keeping the same local date in the target time zone.
export const ConvertWorkingHoursToSameDateInTimeZone = (workingHours, originalTimeZone, targetTimeZone) => {
    // Ensure workingHours is an array
    const workingHoursArray = Array.isArray(workingHours)
        ? workingHours
        : Object.values(workingHours); // Convert object-like structure to array

    if (!Array.isArray(workingHoursArray)) {
        console.error("Invalid workingHours: Expected an array-like structure, but received:", workingHours);
        return []; // Return an empty array if invalid
    }

    const convertedWorkingHours = workingHoursArray.map((workingHour) => ({
        ...workingHour,
        startTime: ConvertToFixedLocalTime(workingHour.startTime, originalTimeZone, targetTimeZone),
        endTime: ConvertToFixedLocalTime(workingHour.endTime, originalTimeZone, targetTimeZone),
        id: Number(workingHour.id),
        groupId: Number(workingHour.groupId),
        parentId: workingHour.parentId ? Number(workingHour.parentId) : null,
    }));

    return convertedWorkingHours;
};



/**
* Convert UTC time (e.g., "17:00") to local time in 24-hour format (e.g., "09:00").
*/
export const convertUTCToLocal24Hour = (utcTime, userTimeZone) => {
    // Parse the UTC time as a DateTime object
    const utcDateTime = DateTime.fromFormat(utcTime, "HH:mm", { zone: "UTC" });

    // Convert to the user's local time zone
    const localDateTime = utcDateTime.setZone(userTimeZone.timezone);

    // Format back to "HH:mm" (24-hour format)
    return localDateTime.toFormat("HH:mm");
};

export const convertDateTimeToUserTimezone = (dateTimeString) => {
    try {
        // Clean up the date string by removing timezone names and extra GMT text.
        const cleanedDateString = dateTimeString
            .replace(/\(.*\)/, '') // Remove timezone names in parentheses
          .replace('GMT', '')    // Remove GMT text (offset is still preserved)
          .trim();

        // Create a Date object from the cleaned string.
        const dateObj = new Date(cleanedDateString);

        // Format the date to include "at" before the time value.
        // Example output: "Mar 17, 2025 at 3:45 PM"
        const formatted = format(dateObj, "MMM d, yyyy 'at' h:mm a");

        return formatted;
    } catch (error) {
        console.error("⏰ Time Machine Meltdown:", error);
        return "";
    }
};

/**
* Convert local time in 12-hour format with AM/PM (e.g., "05:00 PM") 
* to 24-hour format (e.g., "17:00").
*/
export const ConvertLocal12To24Hour = (localTime, timeZone) => {
    if (!localTime) return '';
    // If localTime is already a Dayjs object, simply format it.
    if (typeof localTime === 'object' && localTime.format) {
        return localTime.format("HH:mm");
    }
    // Already in 24-hour format.
    if (typeof localTime === "string" && /^([01]\d|2[0-3]):([0-5]\d)$/.test(localTime.trim())) {
        return localTime.trim();
    }
    // If localTime is a string and equals "all day" (case-insensitive), return it directly.
    if (typeof localTime === 'string' && localTime.trim().toLowerCase() === 'all day') {
        return 'All Day';
    }
    // Otherwise, assume it's a string in "hh:mm a" format.
    const dt = DateTime.fromFormat(localTime, "hh:mm a", { zone: timeZone?.timezone || "UTC" });
    return dt.isValid ? dt.toFormat("HH:mm") : '';
};

/**
* Convert local time in 24-hour format (e.g., "17:00")
* to 12-hour format with AM/PM (e.g., "05:00 PM").
*/
export const ConvertLocal24To12Hour = (localTime, userTimeZone) => {
    // Parse the local time in 24-hour format
    const localDateTime = DateTime.fromFormat(localTime, "HH:mm", {
        zone: userTimeZone.timezone,
    });

    // Format back to 12-hour time with AM/PM
    return localDateTime.toFormat("hh:mm a");
};


/**
 * Convert UTC time (e.g., "17:00") to local time in 12-hour format with AM/PM (e.g., "05:00 PM").
 */
export const ConvertUTCToLocal12Hour = (utcTime, userTimeZone) => {
    if (!utcTime) return "";
    if (typeof utcTime === "string" && utcTime.trim().toLowerCase() === "all day") {
        return "All Day";
    }
    if (!userTimeZone?.timezone) {
        return utcTime;
    }
    // If value is already in local 12-hour text, keep it.
    if (typeof utcTime === "string") {
        const maybeLocal = DateTime.fromFormat(utcTime.trim().toLowerCase(), "hh:mm a");
        if (maybeLocal.isValid) {
            return maybeLocal.toFormat("hh:mm a");
        }
    }
    // Parse the UTC time as a DateTime object
    const utcDateTime = DateTime.fromFormat(utcTime, "HH:mm", { zone: "UTC" });
    if (!utcDateTime.isValid) {
        return utcTime;
    }

    // Convert to the user's local time zone
    const localDateTime = utcDateTime.setZone(userTimeZone.timezone);
    if (!localDateTime.isValid) {
        return utcTime;
    }

    // Format back to "hh:mm a" (12-hour format with AM/PM)
    return localDateTime.toFormat("hh:mm a");
};
